import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompatibilityPercentageData } from "../../../models/interfaces";
import compatibilityComponentsStyles from "./compatibilityComponents.styles";

interface CustomProps {
  percentageData: CompatibilityPercentageData;
}

const CompatibilityPercentage = (props: CustomProps) => {
  const classes = compatibilityComponentsStyles;
  const { t } = useTranslation();

  const getColorBasedOnValue = (value: number) => {
    if (value >= 0 && value < 30) {
      return "#ff000e";
    } else if (value >= 30 && value < 50) {
      return "#eaff00";
    } else if (value >= 50 && value < 70) {
      return "#8fff5a";
    } else if (value >= 70 && value <= 100) {
      return "#3bb63b";
    }
  };

  const getLabelBasedOnValue = (value: number) => {
    return value >= 70
      ? t("strongMatch")
      : value >= 50
      ? t("moderateMatch")
      : value >= 30
      ? t("weakMatch")
      : t("noMatch");
  };

  const PercentageCard = ({
    title,
    value,
    description,
  }: {
    title: string;
    value: number;
    description: string;
  }) => {
    const bgColor = getColorBasedOnValue(value);

    return (
      <Box sx={classes.percentageCard}>
        <Box sx={classes.percentageCardContent}>
          <Typography sx={classes.percentageHeader}>{title}</Typography>
          <Typography
            sx={{
              ...classes.percentageValue,
              color: getColorBasedOnValue(value),
            }}
          >
            {value.toFixed(2)}
            <span
              style={{
                ...classes.percentageSymbol,
                color: getColorBasedOnValue(value),
              }}
            >
              %
            </span>
          </Typography>
          <Typography sx={classes.resultText}>
            {getLabelBasedOnValue(value)}
          </Typography>
          <Box sx={classes.progressBarContainer}>
            <Box
              sx={{
                ...classes.progressBar,
                width: `${value}%`,
                backgroundColor: bgColor,
              }}
            />
          </Box>
        </Box>
        <Box sx={classes.infoNote}>
          <InfoOutlinedIcon sx={classes.infoNoteIcon} />
          <Typography sx={classes.infoNoteText}>{description}</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Grid container spacing={3} sx={{ placeContent: "center" }}>
      <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
        <Box
          sx={{
            ...classes.percentageCard,
            ...classes.totalCompatibility,
            ...classes.totalPercentageCard,
          }}
        >
          <Typography sx={{ ...classes.percentageHeader, fontSize: "2rem" }}>
            {t("totalCompatibility")}
          </Typography>
          <Typography
            sx={{
              ...classes.percentageValue,
              color: getColorBasedOnValue(props.percentageData.totalPercentage),
            }}
          >
            {props.percentageData.totalPercentage}
            <span style={classes.percentageSymbol}>%</span>
          </Typography>
          <Typography sx={classes.resultText}>
            {getLabelBasedOnValue(props.percentageData.totalPercentage)}
          </Typography>
          <Box sx={classes.progressBarContainer}>
            <Box
              sx={{
                ...classes.progressBar,
                width: `${props.percentageData.totalPercentage}%`,
                backgroundColor: getColorBasedOnValue(
                  props.percentageData.totalPercentage
                ),
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Other Percentage Cards */}
      <Grid item xl={4} lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
        <PercentageCard
          title={t("physicalHeader")}
          description={t("physicalDesc")}
          value={props.percentageData.physical}
        />
      </Grid>
      <Grid item xl={4} lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
        <PercentageCard
          title={t("emotionalHeader")}
          description={t("emotionalDesc")}
          value={props.percentageData.emotional}
        />
      </Grid>
      <Grid item xl={4} lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
        <PercentageCard
          title={t("soulHeader")}
          description={t("soulDesc")}
          value={props.percentageData.soul}
        />
      </Grid>
      <Grid item xl={4} lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
        <PercentageCard
          title={t("destinyHeader")}
          description={t("destinyDesc")}
          value={props.percentageData.destiny}
        />
      </Grid>
      <Grid item xl={4} lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
        <PercentageCard
          title={t("marriageHeader")}
          description={t("marriageDesc")}
          value={props.percentageData.marriage}
        />
      </Grid>
    </Grid>
  );
};

export default CompatibilityPercentage;
